/** F-82: Support (Suporte). The shapes follow `/api/support/*`; dates are UTC ISO strings. */

export type SupportKind = 'MAINTENANCE' | 'REQUEST'
export type SupportCategory = 'FACILITIES' | 'CLEANING' | 'EQUIPMENT' | 'INTERNET' | 'ACCESS' | 'BILLING' | 'OTHER'
export type SupportStatus = 'OPEN' | 'IN_PROGRESS' | 'WAITING_REQUESTER' | 'RESOLVED' | 'CANCELED'
export type SupportPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
export type SupportSlaUnit = 'hours' | 'days'

export const SUPPORT_KINDS: SupportKind[] = ['MAINTENANCE', 'REQUEST']
export const SUPPORT_CATEGORIES: SupportCategory[] = ['FACILITIES', 'CLEANING', 'EQUIPMENT', 'INTERNET', 'ACCESS', 'BILLING', 'OTHER']
export const SUPPORT_STATUSES: SupportStatus[] = ['OPEN', 'IN_PROGRESS', 'WAITING_REQUESTER', 'RESOLVED', 'CANCELED']
export const SUPPORT_PRIORITIES: SupportPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT']
export const SUPPORT_OPEN_STATUSES: SupportStatus[] = ['OPEN', 'IN_PROGRESS', 'WAITING_REQUESTER']

export interface SupportPerson {
  id: string
  name?: string
  displayName?: string
  avatar?: string | null
  avatarUrl?: string | null
  removed?: boolean
}

export interface SupportPhoto {
  id: string
  mediaId: string
  position?: number
  /** Signed, short-lived link; only on the detail. */
  url?: string
  thumbnailUrl?: string | null
}

export interface SupportTicketSla {
  firstResponseDueAt: string | null
  resolveDueAt: string
  /** The clock stops while the ticket waits for the resident. */
  paused: boolean
  /** Team only. */
  overdue?: boolean
  firstResponseOverdue?: boolean
  resolveOverdue?: boolean
}

export interface SupportBusinessHours {
  /** 0 = Sunday ... 6 = Saturday, in the community's time zone. */
  days: number[]
  /** "HH:MM". */
  start: string
  end: string
}

export interface SupportTicket {
  id: string
  number: number
  spaceId: string
  kind: SupportKind
  category: SupportCategory
  status: SupportStatus
  priority: SupportPriority
  title: string
  description: string
  requesterId: string
  requester?: SupportPerson | null
  openedByTeam?: boolean
  assigneeId: string | null
  assignee?: SupportPerson | null
  unassigned?: boolean
  resourceId: string | null
  resource?: { id: string; name: string; imageUrl?: string | null; removed?: boolean } | null
  locationText: string | null
  firstResponseAt: string | null
  resolvedAt: string | null
  canceledAt: string | null
  reopenedCount: number
  lastActivityAt: string
  createdAt: string
  updatedAt: string
  photoCount?: number
  photos?: SupportPhoto[]
  /** The deadlines of an open ticket; `null` when the SLA is off or the ticket is closed. */
  sla?: SupportTicketSla | null
  canComment?: boolean
  canCancel?: boolean
  canReopen?: boolean
  canEdit?: boolean
  canAssign?: boolean
  canAnswer?: boolean
  canNote?: boolean
  canAddPhoto?: boolean
  canDelete?: boolean
  /** Phase 2 (owner and admin): take the ticket's room out of use, or give it back. */
  canCloseResource?: boolean
  canReopenResource?: boolean
  /** The room closure this ticket caused; the team only. */
  closure?: SupportClosure | null
  /** F-95: `ROUND` when an inspection round opened the ticket. */
  origin?: SupportOrigin
  round?: { id: string; sectorName: string | null; startedAt: string } | null
}

export type SupportOrigin = 'REQUEST' | 'ROUND'

export interface SupportClosure {
  exceptionId: string
  resourceId: string
  startsAt: string
  endsAt: string
  startsAtLocal: string
  endsAtLocal: string
  reason: string | null
  active: boolean
  /** The ticket is resolved and the room is still closed: offer to give it back. */
  offerReopen: boolean
}

export interface SupportClosureInput {
  /** The community's local time: `2026-11-03T14:00`. */
  startsAt: string
  endsAt: string
  reason?: string
}

export interface SupportClosureBooking { id: string; memberId: string; startsAt: string; endsAt: string; status: string; startsAtLocal: string }

export interface SupportClosurePreview {
  ticketId: string
  resourceId: string
  resourceName: string
  startsAtLocal: string
  endsAtLocal: string
  reason: string | null
  affectedBookings: number
  creditsToRefund: number
  bookings: SupportClosureBooking[]
}

export interface SupportReportGroup {
  key: string
  label?: string | null
  opened: number
  resolved: number
  open: number
  overdue: number
  reopened: number
  avgFirstResponseMinutes: number | null
  avgResolutionMinutes: number | null
  onTimePercent: number | null
}

export interface SupportReport {
  period: { from: string; to: string; timeZone: string }
  totals: SupportReportGroup
  byCategory: SupportReportGroup[]
  byAssignee: SupportReportGroup[]
  byResource: SupportReportGroup[]
}

export interface SupportTimelineEntry {
  id: string
  kind: 'MESSAGE' | 'INTERNAL_NOTE' | 'SYSTEM'
  body: string
  metadata?: { field?: string; from?: unknown; to?: unknown; [key: string]: unknown } | null
  author?: SupportPerson | null
  authorIsTeam?: boolean
  createdAt: string
  photos?: SupportPhoto[]
  canDelete?: boolean
}

export interface SupportTicketDetail extends SupportTicket {
  timeline: SupportTimelineEntry[]
}

export interface SupportTarget { value: number; unit: SupportSlaUnit }
export interface SupportSlaCategory { firstResponse: SupportTarget; resolution: SupportTarget }

export interface SupportSelection {
  /** Member tags, by name. */
  tags: string[]
  groupIds: string[]
  memberIds: string[]
}

export interface SupportResponders extends SupportSelection {
  byCategory: Partial<Record<SupportCategory, SupportSelection>>
}

/** `GET /api/support/config`: what the open form needs; the team also gets who answers. */
export interface SupportPublicConfig {
  spaceId: string
  title?: string
  allowUrgentFlag: boolean
  maxOpenPerMember: number
  locationHint: string
  hasBookings: boolean
  sla: { enabled: boolean; businessHours?: SupportBusinessHours; categories: Partial<Record<SupportCategory, SupportSlaCategory>> }
  timeZone?: string
  categories: SupportCategory[]
  kinds: SupportKind[]
  canAnswerCategories?: SupportCategory[]
  responders?: SupportResponders
  defaultAssigneeId?: string | null
}

export interface SupportSummary {
  open: number
  inProgress: number
  waitingRequester: number
  unassigned: number
  mine: number
  total: number
  overdue: number
}

export interface SupportListParams {
  spaceId?: string
  kind?: SupportKind
  status?: SupportStatus[]
  priority?: SupportPriority[]
  category?: SupportCategory[]
  assigneeId?: string
  unassigned?: boolean
  mine?: boolean
  overdue?: boolean
  dueToday?: boolean
  resourceId?: string
  origin?: SupportOrigin
  q?: string
  sort?: 'activity' | 'priority'
  page?: number
  limit?: number
}

export interface SupportPage {
  items: SupportTicket[]
  pagination: { page: number; totalPages: number; total: number } | null
}

export interface SupportCreateInput {
  spaceId: string
  kind: SupportKind
  category: SupportCategory
  title: string
  description: string
  resourceId?: string
  locationText?: string
  urgent?: boolean
  priority?: SupportPriority
  requesterId?: string
  mediaIds?: string[]
  clientRequestId?: string
}

export interface SupportUpdateInput {
  status?: SupportStatus
  priority?: SupportPriority
  kind?: SupportKind
  category?: SupportCategory
  assigneeId?: string | null
  resourceId?: string | null
  locationText?: string | null
  title?: string
  /** Sent with a resolution: the "what was done" answer that goes out in the e-mail. */
  message?: string
}

/** What the space edit and the wizard hold while the owner fills the Support setup. */
export interface SupportModuleConfig {
  allowUrgentFlag: boolean
  maxOpenPerMember: number
  locationHint: string
  defaultAssigneeId: string | null
  responders: SupportResponders
  sla: { enabled: boolean; businessHours: SupportBusinessHours; categories: Record<SupportCategory, SupportSlaCategory> }
}

/* ---------------------------------------------------------------------- */
/* F-95: inspection rounds                                                 */
/* ---------------------------------------------------------------------- */

/** A place a round checks: a Bookings resource or a free label. */
export interface InspectionSpot {
  id: string
  sectorId: string
  name: string
  label: string | null
  resourceId: string | null
  resource: { id: string; name: string; removed: boolean } | null
  position: number
}

export interface InspectionSector {
  id: string
  spaceId: string
  name: string
  position: number
  spots: InspectionSpot[]
  openRound: { id: string; startedAt: string } | null
  lastCompletedAt: string | null
  createdAt: string
}

export type RoundItemStatus = 'OK' | 'PROBLEM' | 'PENDING'
export type RoundStatus = 'IN_PROGRESS' | 'COMPLETED'

export interface RoundTicketRef { id: string; number: number; status: SupportStatus; title: string }

export interface RoundItem {
  id: string
  spotId: string
  spotName: string
  resourceId: string | null
  status: RoundItemStatus
  description: string | null
  ticketId: string | null
  ticket: RoundTicketRef | null
  checkedBy: SupportPerson | null
  checkedAt: string | null
}

export interface RoundCounts { total: number; ok: number; problem: number; pending: number; unchecked: number }

/** While open, the checklist has every live spot of the sector; once completed, only what was recorded (`spot: null`). */
export interface RoundChecklistEntry { spot: InspectionSpot | null; item: RoundItem | null }

export interface InspectionRound {
  id: string
  spaceId: string
  sectorId: string
  sectorName: string | null
  status: RoundStatus
  startedAt: string
  startedBy: SupportPerson | null
  completedAt: string | null
  completedBy: SupportPerson | null
  counts: RoundCounts
  checklist?: RoundChecklistEntry[]
  canMark: boolean
  canComplete: boolean
}

export interface RoundPage {
  items: InspectionRound[]
  pagination: { page: number; totalPages: number; total: number } | null
}

export interface RoundItemInput {
  spotId: string
  status: 'OK' | 'PROBLEM'
  description?: string
  mediaIds?: string[]
}

export interface ResourceHistory {
  resource: { id: string; name: string; removed: boolean }
  rounds: Array<{
    roundId: string
    sectorName: string | null
    startedAt: string
    completedAt: string | null
    status: RoundItemStatus
    description: string | null
    ticket: RoundTicketRef | null
    checkedBy: SupportPerson | null
    checkedAt: string | null
  }>
  tickets: Array<{
    id: string
    number: number
    spaceId: string
    kind: SupportKind
    category: SupportCategory
    status: SupportStatus
    priority: SupportPriority
    title: string
    origin: SupportOrigin
    createdAt: string
    resolvedAt: string | null
    roundId: string | null
  }>
}
